// Whether the announcement above the page has been put away in this session.
//
// Separated from the component because every rule here is a decision with a way
// of being wrong, and none of them needs a browser to be proved:
//
//   * A NOTICE IS IDENTIFIED BY ITS TEXT. The key is a digest of the message, so
//     a new announcement is shown again to somebody who dismissed the last one
//     and nothing has to be cleared when one replaces another.
//   * WHAT IS REMEMBERED IS A KEY, NOT A FLAG. Holding the key that was
//     dismissed means "is this one dismissed" is a comparison rather than a
//     piece of state somebody has to reset, and the read below can only ever
//     set the key it just found — so a dismissal clicked in the same tick as a
//     read cannot be undone by it.
//   * STORAGE MAY NOT BE THERE. Reaching for it throws in a private window and
//     where site data is blocked, and it can answer with nothing at all. Every
//     path here ends in "the notice stays up", which is the safe direction: an
//     announcement is published during an incident, and showing it once too
//     often costs less than not showing it.

// THE PREFIX IS ONE DECLARATION, ON PURPOSE, AND IT IS THE ONLY ONE.
//
// Every key this module makes is built from it, nothing else in the file spells
// the string out, and it is exported. The value is also published to readers in
// the cookie-and-storage table of `$lib/consent/categories`, so it is adopted
// from there rather than typed a second time: two spellings of one storage key
// would let the table a visitor is shown drift away from the key actually
// written. One line, and every key, every read and every write follows.
import { NOTICE_STORAGE_PREFIX } from "$lib/consent/categories";

export { NOTICE_STORAGE_PREFIX };

/** The little of `Storage` this needs, so a test hands it an object. */
export interface NoticeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The value written; read back by equality so anything else means "not dismissed". */
const DISMISSED = "1";

/**
 * The storage key for an announcement's text.
 *
 * A short, stable digest rather than the message itself, so the key does not
 * grow with the message and a changed message is a different key.
 */
export function noticeKeyFor(text: string): string {
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) | 0;
  }
  return `${NOTICE_STORAGE_PREFIX}${hash}`;
}

/**
 * This session's storage, or null where there is none to reach.
 *
 * The property access itself throws in some configurations, which is why it is
 * inside the guard rather than read at module level.
 */
export function browserNoticeStorage(): NoticeStorage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * The key to remember as dismissed after consulting storage, or null.
 *
 * Null for every reason there is — no notice yet, no storage, nothing written,
 * something else written, a store that threw — because they all mean the same
 * thing to the page: show it.
 */
export function readDismissedKey(
  storageKey: string | null,
  storage: NoticeStorage | null,
): string | null {
  if (storageKey === null || storage === null) return null;
  try {
    return storage.getItem(storageKey) === DISMISSED ? storageKey : null;
  } catch {
    return null;
  }
}

/**
 * Puts an announcement away for the rest of the session; silent where it
 * cannot.
 *
 * It takes the announcement's TEXT rather than a key somebody composed. Two
 * reasons, and the second is the one that is easy to miss:
 *
 *   * the key is this module's business. A caller that builds one can build a
 *     wrong one, and a wrong key is a dismissal that never reads back — which
 *     looks exactly like storage being unavailable and is silent either way.
 *   * a guard that walks this app for everything it writes to a device can
 *     follow `noticeKeyFor` to the prefix above and check the value against
 *     what is published; it cannot follow an opaque string handed in from
 *     outside, and what it reports then is "nothing to check".
 */
export function writeDismissedNotice(text: string, storage: NoticeStorage | null): void {
  if (storage === null) return;
  try {
    storage.setItem(noticeKeyFor(text), DISMISSED);
  } catch {
    // Dismissing for this page is still better than not dismissing at all.
  }
}

/** Whether the notice now in hand is the one this session put away. */
export function noticeIsDismissed(storageKey: string | null, dismissedKey: string | null): boolean {
  return storageKey !== null && dismissedKey === storageKey;
}
